import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getCustomerName, getCustomerEmail } from "@/lib/safe-access";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  MoreHorizontal,
  Download,
  ExternalLink,
  Plus,
  Send,
} from "lucide-react";
import { useInvoices } from "@/api/invoices";
import { AddOrdersToInvoiceDialog } from "./add-orders-to-invoice-dialog";
import { ReviewInvoiceDialog } from "./review-invoice-dialog";
import { isUnsentInvoice } from "./invoice-send-eligibility";
import { InvoiceDueDateDisplay } from "./invoice-due-date-display";

export function InvoicesTable() {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [addOrdersDialogOpen, setAddOrdersDialogOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null
  );
  const [reviewInvoiceId, setReviewInvoiceId] = useState<string | null>(null);

  const { data: invoicesResponse } = useInvoices(page, limit);

  const handleAddOrders = (invoiceId: string) => {
    setSelectedInvoiceId(invoiceId);
    setAddOrdersDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice Details</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Orders</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoicesResponse?.data.map((invoice) => (
              <TableRow key={invoice.id}>
                <TableCell>
                  <div>
                    {invoice.stripeInvoiceData?.number ? (
                      <p className="font-medium">
                        #{invoice.stripeInvoiceData.number}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500 italic">
                        Generated after invoice created
                      </p>
                    )}
                    <p className="text-sm text-gray-500">
                      {new Date(invoice.createdAt).toLocaleDateString()}
                    </p>
                    <InvoiceDueDateDisplay
                      invoice={invoice}
                      className="text-xs text-gray-400"
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">
                      {getCustomerName(invoice.customer)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {getCustomerEmail(invoice.customer)}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-medium">
                    {invoice.totalCents != null
                      ? `$${(invoice.totalCents / 100).toFixed(2)}`
                      : "—"}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="capitalize">
                    {invoice.status === "DRAFT"
                      ? "Draft · awaiting Admin review"
                      : invoice.status === "OPEN" && isUnsentInvoice(invoice)
                        ? "Unsent · review required"
                        : invoice.status}
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">{invoice.totalOrders} orders</p>
                    {invoice.orderDateRange && (
                      <p className="text-xs text-gray-400">
                        {new Date(
                          invoice.orderDateRange.start
                        ).toLocaleDateString()}{" "}
                        -{" "}
                        {new Date(
                          invoice.orderDateRange.end
                        ).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {invoice.status === "DRAFT" &&
                        !invoice.stripeInvoiceId && (
                          <DropdownMenuItem
                            onClick={() => handleAddOrders(invoice.id)}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Add Orders
                          </DropdownMenuItem>
                        )}
                      {isUnsentInvoice(invoice) && (
                        <DropdownMenuItem
                          onClick={() => setReviewInvoiceId(invoice.id)}
                        >
                          <Send className="mr-2 h-4 w-4" />
                          Review & Send
                        </DropdownMenuItem>
                      )}
                      {invoice.stripeInvoiceData?.hosted_invoice_url !=
                        null && (
                        <DropdownMenuItem
                          onClick={() =>
                            window.open(
                              invoice.stripeInvoiceData?.hosted_invoice_url ??
                                undefined,
                              "_blank"
                            )
                          }
                        >
                          <ExternalLink className="mr-2 h-4 w-4" />
                          View Invoice
                        </DropdownMenuItem>
                      )}
                      {invoice.stripeInvoiceData?.invoice_pdf != null && (
                        <DropdownMenuItem
                          onClick={() =>
                            window.open(
                              invoice.stripeInvoiceData?.invoice_pdf ??
                                undefined,
                              "_blank"
                            )
                          }
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Download PDF
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-2">
        <div className="text-sm text-gray-500">
          Showing {(page - 1) * limit + 1} to{" "}
          {Math.min(page * limit, invoicesResponse?.metadata.total ?? 0)} of{" "}
          {invoicesResponse?.metadata.total ?? 0} results
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page + 1)}
            disabled={page >= (invoicesResponse?.metadata.totalPages ?? 0)}
          >
            Next
          </Button>
        </div>
      </div>

      {selectedInvoiceId && (
        <AddOrdersToInvoiceDialog
          open={addOrdersDialogOpen}
          onOpenChange={setAddOrdersDialogOpen}
          invoiceId={selectedInvoiceId}
        />
      )}
      <ReviewInvoiceDialog
        invoiceId={reviewInvoiceId}
        onClose={() => setReviewInvoiceId(null)}
      />
    </div>
  );
}
